import { useMemo } from 'react';

import dayjs from 'dayjs';

import { mapOverviewToReportingUsageDetail } from '@cast/features-cluster-shared-utils';
import { useOrganizationOverviewQuery } from '@cast/shared-cost-data-access';

type UseReportingPlatformUsageDetailArgs = {
  periodFrom: string; // YYYY-MM-DD
  periodTo: string; // YYYY-MM-DD
  enabled?: boolean;
};

/**
 * Reporting chart/table data via cost-report organization overview.
 * Temporary until billing `platform-usage-detail?feature=reporting` exists.
 *
 * Boundary timestamps and daily bucketing both use the browser-local
 * timezone so that a selected local day maps to a single daily bucket.
 */
export const useReportingPlatformUsageDetail = ({
  periodFrom,
  periodTo,
  enabled = true,
}: UseReportingPlatformUsageDetailArgs) => {
  const rangeIso = useMemo(
    () => ({
      startTime: dayjs(periodFrom).startOf('day').toISOString(),
      endTime: dayjs(periodTo).endOf('day').toISOString(),
    }),
    [periodFrom, periodTo]
  );

  const { data, isLoading, isError, refetch, isPending } =
    useOrganizationOverviewQuery({
      ...rangeIso,
      enabled: enabled && !!periodFrom && !!periodTo,
    });

  const detail = useMemo(() => mapOverviewToReportingUsageDetail(data), [data]);

  return {
    detail,
    isLoading: isLoading || isPending,
    isError,
    refetch,
  };
};
